// Converts a Firestore story document (client or Admin SDK) into the plain
// Story shape. Timestamps are duck-typed so both SDKs work.
import {
  AUDIENCE_TAGS,
  CLOUDS,
  CONTENT_TYPES,
  RELEASE_STATUSES,
  TOPICS,
  type AudienceTag,
  type Cloud,
  type SourceLink,
  type Story,
} from "./model.ts";
import { FLAG_STATUSES, type FlagStatus } from "./flags.ts";
import { PRODUCT_BY_ID } from "./products.ts";
import { ROLES } from "./roles.ts";

type TimestampLike = { toMillis(): number };
const isTs = (v: unknown): v is TimestampLike =>
  typeof v === "object" && v !== null && typeof (v as TimestampLike).toMillis === "function";
const ms = (v: unknown): number | null => (isTs(v) ? v.toMillis() : null);
const str = (v: unknown, fallback = ""): string => (typeof v === "string" ? v : fallback);
const oneOf = <T extends string>(list: readonly T[], v: unknown, fallback: T): T =>
  list.includes(v as T) ? (v as T) : fallback;

/** Returns null for documents that are not displayable (missing required fields). */
export function storyFromData(id: string, d: Record<string, unknown>): Story | null {
  const s = (d.summaries ?? {}) as Record<string, unknown>;
  const summaries = {
    plainEnglish: str(s.plainEnglish),
    businessImpact: str(s.businessImpact),
    technicalDetail: str(s.technicalDetail),
  };
  const sortAt = ms(d.sortAt) ?? ms(d.publishedAt) ?? ms(d.firstSeenAt);
  if (!str(d.title) || !str(d.canonicalUrl) || sortAt === null) return null;
  if (!summaries.plainEnglish || !summaries.businessImpact || !summaries.technicalDetail) return null;
  if (!TOPICS.includes(d.primaryTopic as never)) return null;

  return {
    id,
    title: str(d.title),
    canonicalUrl: str(d.canonicalUrl),
    sourceId: str(d.sourceId),
    sourceName: str(d.sourceName, "Unknown source"),
    contentType: oneOf(CONTENT_TYPES, d.contentType, "blog"),
    primaryTopic: d.primaryTopic as Story["primaryTopic"],
    topicTags: Array.isArray(d.topicTags) ? d.topicTags.filter((t): t is string => typeof t === "string") : [],
    audiences: Array.isArray(d.audiences)
      ? d.audiences.filter((a): a is AudienceTag => AUDIENCE_TAGS.includes(a as AudienceTag))
      : [],
    publishedAt: ms(d.publishedAt),
    sourceUpdatedAt: ms(d.sourceUpdatedAt),
    sortAt,
    summaries,
    whyItMatters: str(d.whyItMatters),
    whyProvenance: d.whyProvenance === "ai_generated" ? "ai_generated" : "editorial",
    whyModel: typeof d.whyModel === "string" ? d.whyModel : null,
    estimatedReadMinutes:
      typeof d.estimatedReadMinutes === "number" && d.estimatedReadMinutes > 0 ? Math.round(d.estimatedReadMinutes) : 1,
    releaseStatus: oneOf(RELEASE_STATUSES, d.releaseStatus, "unknown"),
    clouds: Array.isArray(d.clouds) ? d.clouds.filter((c): c is Cloud => CLOUDS.includes(c as Cloud)) : [],
    availabilityNotes: typeof d.availabilityNotes === "string" ? d.availabilityNotes : null,
    availabilityVerifiedAt: ms(d.availabilityVerifiedAt),
    availabilitySourceUrl: typeof d.availabilitySourceUrl === "string" ? d.availabilitySourceUrl : null,
    sourceLinks: Array.isArray(d.sourceLinks)
      ? (d.sourceLinks as SourceLink[]).filter((l) => l && typeof l.url === "string" && typeof l.label === "string")
      : [],
    summaryProvenance: oneOf(["editorial", "ai_assisted", "sample", "source_excerpt"] as const, d.summaryProvenance, "editorial"),
    isSample: d.isSample === true,
    products: Array.isArray(d.products) ? d.products.filter((p): p is string => typeof p === "string" && PRODUCT_BY_ID.has(p)) : [],
    docsLabel: decodeDocsLabel(d.docsLabel),
    docsClouds: Array.isArray(d.docsClouds) ? d.docsClouds.filter((c): c is Cloud => CLOUDS.includes(c as Cloud)) : [],
    ...(Array.isArray(d.roles) ? { roles: ROLES.filter((r) => (d.roles as unknown[]).includes(r)) } : {}),
  };
}

function decodeDocsLabel(v: unknown): Story["docsLabel"] {
  const l = v as { status?: unknown; sourceUrl?: unknown } | null;
  if (!l || typeof l.sourceUrl !== "string" || !FLAG_STATUSES.includes(l.status as FlagStatus)) return null;
  return { status: l.status as FlagStatus, sourceUrl: l.sourceUrl };
}
